
import { Link } from 'react-router-dom';

export default function EmotionalLaborPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-red-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-pink-600 via-rose-600 to-red-600 text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link to="/motivation" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
              <i className="ri-arrow-left-line mr-2"></i>
              動機付け一覧に戻る
            </Link>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl mb-6">
              <i className="ri-emotion-line text-4xl"></i>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">感情労働</h1>
            <p className="text-xl text-white/90">Emotional Labor</p>
            <p className="text-lg text-white/80 mt-2">提唱者：Hochschild</p>
          </div>
        </div>
      </section>

      {/* Core Message */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-pink-500 to-rose-500 rounded-2xl shadow-2xl p-8 md:p-12 text-white text-center">
              <h2 className="text-3xl font-bold mb-4">感情を調整しながら働く職業は消耗しやすい</h2>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto space-y-12">
            {/* 理論の概要 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-pink-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-book-open-line text-xl text-white"></i>
                </div>
                理論の概要
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                感情労働とは、仕事の一部として<strong className="text-pink-600">自分の感情を管理・調整すること</strong>を求められる労働のことです。薬剤師は、患者さんに対して常に親切で丁寧な対応を求められるため、典型的な感情労働者です。
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl p-6 border border-pink-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-mask-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">表層演技</h3>
                  <p className="text-sm text-gray-600 mb-2">Surface Acting</p>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    本当の感情を隠して、求められる感情を「演じる」こと
                  </p>
                  <div className="mt-3 p-2 bg-pink-100 rounded">
                    <p className="text-xs text-pink-700">例：イライラしていても笑顔で対応する</p>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-rose-50 to-red-50 rounded-xl p-6 border border-rose-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-rose-600 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-heart-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">深層演技</h3>
                  <p className="text-sm text-gray-600 mb-2">Deep Acting</p>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    自分の感情自体を変えようと努力すること
                  </p>
                  <div className="mt-3 p-2 bg-rose-100 rounded">
                    <p className="text-xs text-rose-700">例：患者さんの立場に立って共感しようとする</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 薬局運営への活用 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-pink-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-rose-500 to-pink-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-hospital-line text-xl text-white"></i>
                </div>
                薬局運営への活用
              </h2>
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl p-6 border border-pink-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-scales-2-line text-pink-600 mr-2"></i>
                    薬剤師業務の負荷再定義
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    薬剤師の仕事は、調剤や服薬指導だけでなく、<strong className="text-pink-600">感情労働としての負荷</strong>も大きいことを認識する必要があります。
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-pink-100">
                    <h4 className="font-bold text-gray-900 mb-2 text-sm">感情労働の場面</h4>
                    <ul className="text-xs text-gray-600 space-y-1">
                      <li>・不安な患者さんへの丁寧な説明</li>
                      <li>・クレーム対応</li>
                      <li>・医師との調整・交渉</li>
                      <li>・終末期患者さんへの対応</li>
                      <li>・認知症患者さんへの繰り返しの説明</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-rose-50 to-red-50 rounded-xl p-6 border border-rose-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-award-line text-rose-600 mr-2"></i>
                    クレーム・調整業務の評価対象化
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    感情労働は「見えない労働」になりがちです。これを<strong className="text-rose-600">評価の対象</strong>として可視化することが重要です。
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start p-3 bg-white rounded-lg border border-rose-100">
                      <i className="ri-checkbox-circle-fill text-green-500 mr-3 mt-0.5"></i>
                      <span className="text-gray-700 text-sm">クレーム対応の件数・内容を記録する</span>
                    </div>
                    <div className="flex items-start p-3 bg-white rounded-lg border border-rose-100">
                      <i className="ri-checkbox-circle-fill text-green-500 mr-3 mt-0.5"></i>
                      <span className="text-gray-700 text-sm">困難な対応をした職員を評価・ねぎらう</span>
                    </div>
                    <div className="flex items-start p-3 bg-white rounded-lg border border-rose-100">
                      <i className="ri-checkbox-circle-fill text-green-500 mr-3 mt-0.5"></i>
                      <span className="text-gray-700 text-sm">感情労働の負荷を業務分担に反映する</span>
                    </div>
                    <div className="flex items-start p-3 bg-white rounded-lg border border-rose-100">
                      <i className="ri-checkbox-circle-fill text-green-500 mr-3 mt-0.5"></i>
                      <span className="text-gray-700 text-sm">感情を吐き出せる場（ピアサポート）を設ける</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 感情労働からの回復 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-pink-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-heart-pulse-line text-xl text-white"></i>
                </div>
                感情労働からの回復
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl p-5 border border-pink-200">
                  <i className="ri-chat-smile-3-line text-2xl text-pink-600 mb-3"></i>
                  <h3 className="font-bold text-gray-900 mb-2">感情の吐き出し</h3>
                  <p className="text-sm text-gray-600">同僚と感情を共有できる場を設ける</p>
                </div>
                <div className="bg-gradient-to-br from-rose-50 to-red-50 rounded-xl p-5 border border-rose-200">
                  <i className="ri-time-line text-2xl text-rose-600 mb-3"></i>
                  <h3 className="font-bold text-gray-900 mb-2">回復時間の確保</h3>
                  <p className="text-sm text-gray-600">困難な対応の後は休憩を取る</p>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl p-5 border border-red-200">
                  <i className="ri-team-line text-2xl text-red-600 mb-3"></i>
                  <h3 className="font-bold text-gray-900 mb-2">チームでの分担</h3>
                  <p className="text-sm text-gray-600">一人に負荷が集中しないようにする</p>
                </div>
                <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl p-5 border border-pink-200">
                  <i className="ri-medal-line text-2xl text-pink-600 mb-3"></i>
                  <h3 className="font-bold text-gray-900 mb-2">承認・感謝</h3>
                  <p className="text-sm text-gray-600">感情労働の努力を認め、感謝を伝える</p>
                </div>
              </div>
            </div>

            {/* 明日の一手 */}
            <div className="bg-gradient-to-br from-pink-600 via-rose-600 to-red-600 rounded-2xl shadow-2xl p-8 text-white">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <i className="ri-calendar-check-line text-3xl mr-3"></i>
                明日の一手
              </h2>
              <p className="text-lg text-white/90 leading-relaxed mb-6">
                感情労働の負荷が高い職員を見つけ、ねぎらいの言葉をかけましょう。
              </p>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6">
                <p className="text-white font-semibold mb-2">具体的なアクション：</p>
                <p className="text-white/90 leading-relaxed">
                  「さっきの対応、大変だったね。ありがとう」と、困難な対応をした職員に声をかける。
                </p>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center pt-8 border-t border-gray-200">
              <Link to="/motivation/burnout" className="flex items-center text-pink-600 hover:text-pink-700 font-semibold transition-colors">
                <i className="ri-arrow-left-line mr-2"></i>
                バーンアウト理論
              </Link>
              <Link to="/motivation/psychological-reactance" className="flex items-center text-pink-600 hover:text-pink-700 font-semibold transition-colors">
                心理的リアクタンス理論
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
